import { Component, OnInit, Input, ViewChild, OnChanges, OnDestroy, AfterViewInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { trigger, state, style } from '@angular/animations';
import { Subscription } from 'rxjs';

import { FileDetail } from '@classes/file/file-detail';
import { ProcessState } from '@enums/process-state.enum';
import { FileSource } from '@enums/file-source.enum';
import { FileService } from '@services/file.service';
import { UiService } from '@services/ui.service';
import { AuthUser } from '@classes/auth/auth-user';
import { AuthService } from '@services/auth.service';
import { LicenceRoleType } from '@enums/licence-role-type.enum';
import { PaginationService } from '@services/pagination.service';

@Component({
    selector: 'app-files-table',
    templateUrl: './files-table.component.html',
    styleUrls: ['./files-table.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0', display: 'none' })),
            state('expanded', style({ height: '*' }))
        ])
    ]
})
export class FilesTableComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
    @Input() files: FileDetail[];
    @Input() source: FileSource;

    @ViewChild(MatSort, { static: true }) sort: MatSort;
    @ViewChild(MatPaginator, {static: false}) paginator: MatPaginator;

    private subscription = new Subscription();

    displayedColumns: string[];
    dataSource: MatTableDataSource<FileDetail>;
    processState = ProcessState;
    showedId: number;
    isLoading: boolean;
    user: AuthUser;
    RoleType = LicenceRoleType;

    constructor(
        private fileSrv: FileService,
        private uiSrv: UiService,
        private authSrv: AuthService,
        private paginationSrv: PaginationService
    ) {}

    ngOnInit() {
        this.initSubscriptions();
        this.user = this.authSrv.getAuthUser();
    }

    ngOnChanges(changes): void {
        this.initTable();
        this.initFilter()

        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    ngAfterViewInit(): void {
        this.paginationSrv.setText(this.paginator);

        this.dataSource.sortingDataAccessor = this.sortingDataAccessor;
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
      }

    onToggleSelected(selId: number): void {
        this.showedId = this.showedId === selId ? null : selId;
    }

    onDeleteFile(id: number): void {
        this.uiSrv.confirmDialog('main.dialogs.confirmDeleteFile.summary').afterClosed()
        .subscribe((result: boolean) => {
            if(result) {
                this.isLoading = true;
                this.fileSrv.delete(id, this.source).subscribe();
            }
        });
    }

    onApplyFilter(filterValue: string) {
        this.dataSource.filter = filterValue.trim().toLowerCase();
    }

    private initTable(): void {
        switch(this.source) {
            case FileSource.simulation:
                this.displayedColumns = ['name', 'partitioning', 'editedOn', 'uploadedOn', 'uploadedBy', 'status', 'actions'];
                break;
            case FileSource.provider:
                this.displayedColumns = ['name', 'organism', 'type', 'year', 'uploadedOn', 'uploadedBy', 'status', 'actions'];
                break;
            case FileSource.import:
                this.displayedColumns = ['name', 'organism', 'type', 'dataDate', 'importDate', 'status'];
                break;
            default:
                this.displayedColumns = [];
        }

        this.dataSource = new MatTableDataSource(this.files);
        this.dataSource.sortingDataAccessor = this.sortingDataAccessor;
        this.dataSource.sort = this.sort;
    }

    private initFilter(): void {
        this.dataSource.filterPredicate = ((file, filter) =>
            file.name.toLowerCase().includes(filter) ||
            (file.organism && file.organism.text && file.organism.text.shortLabel.toLowerCase().includes(filter)) ||
            (file.partitioning && file.partitioning.text && file.partitioning.text.shortLabel.toLowerCase().includes(filter)) ||
            (file.amountType && file.amountType.toLowerCase().includes(filter)) ||
            (file.year && file.year.toString().includes(filter)) ||
            (file.uploadedBy && file.uploadedBy.toLowerCase().includes(filter))
            );
    }

    private initSubscriptions(): void {
        this.subscription.add(
            this.uiSrv.loadingStateChanged
            .subscribe(loadingState => {
                this.isLoading = loadingState;
            })
        );
    }

    private sortingDataAccessor(item: FileDetail, property: string) : any {
        switch(property) {
            case 'organism': return item.organism?.text?.shortLabel;
            case 'partitioning': return item.partitioning?.text?.shortLabel;
            case 'type': return item.amountType;
            case 'dataDate': return item.uploadedOn;
            case 'importDate': return item.editedOn;
            default: return item[property];
        }
    };

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
